import { useMemo, useCallback, useEffect, useContext } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import useClassicRewardCartPricing from 'hooks/Flights/useClassicRewardCartPricing'
import { useMaldivesBundleRefreshContext } from '../../hooks/MaldivesBundleRefreshContext'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import useUserMultiCartHotelItemData from '../../hooks/useUserMultiCartHotelItemData'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import { MultiCartReviewMachineContext } from '../../MultiCartReviewMachineContext'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'

interface Props {
  multiCartItems: Array<App.MultiCartFlightItem | App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem>
  tileVariant?: 'default' | 'condensed'
  hideTitleAndDates?: boolean
  hideButtons?: boolean
}

function LeHotelTile({
  multiCartItems,
  tileVariant = 'default',
  hideTitleAndDates = false,
  hideButtons = false,
}: Props) {
  const intl = useIntl()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()

  // Separate flight and hotel items
  const hotelItem = useMemo(() => {
    return multiCartItems.find(item => !isFlightItem(item.item)) as App.MultiCartInstantBookingLEHotelItem | undefined
  }, [multiCartItems])

  const flightItems = useMemo(() => {
    return multiCartItems.filter(item => isFlightItem(item.item)) as Array<App.MultiCartFlightItem>
  }, [multiCartItems])

  const item = hotelItem?.item as App.Checkout.InstantBookingLEHotelItem
  const data = useUserMultiCartHotelItemData(item)

  const formattedDateRange = useMemo(() => {
    if (!hotelItem) return false
    const dateRange = formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut)
    if (!dateRange) return false
    return intl.formatMessage(
      {
        id: 'VEyYa4DloTDcmeqy-T5J',
        defaultMessage: '{dateRange} · {duration, plural, one {# night} other {# nights}}',
        description: 'Date range and stay duration for a LE hotel tile in the flight and hotel bundle cart review',
      },
      {
        dateRange,
        duration: hotelItem.duration,
      },
    )
  }, [intl, hotelItem])

  const formattedOccupancy = useMemo(() => {
    if (!hotelItem?.occupancy) return false
    return formatOccupantsShort(hotelItem.occupancy)
  }, [hotelItem?.occupancy])

  const { publicTotal: hotelPublicTotal, memberTotal: hotelMemberTotal } = useMemo(
    () => getUserCartItemDisplayTotals(data.itemTotals),
    [data?.itemTotals],
  )

  const flightTotal = useMemo(() => {
    return flightItems.reduce((total, flightItem) => {
      const flightCheckoutItem = flightItem.item as App.Checkout.FlightItem
      const associatedRoomCount = flightCheckoutItem.bundledItemIds?.length ?? 1
      return total + (getFlightsTotal(flightCheckoutItem) / associatedRoomCount)
    }, 0)
  }, [flightItems])

  const bundledPublicTotal = hotelPublicTotal + flightTotal
  const bundledMemberTotal = hotelMemberTotal === 0 ? 0 : hotelMemberTotal + flightTotal

  // Reward bundle pricing — shared hook computes pts + cash from all reward flights.
  // Disabled while the flight is still refreshing (journey keys may be stale).
  const { refreshingFlightIds } = useMaldivesBundleRefreshContext()
  const rewardFlightRefreshing = flightItems.some(fi => (fi.item as App.Checkout.FlightItem).isClassicReward && refreshingFlightIds.has(fi.item.itemId))
  const rewardFlightCheckoutItems = useMemo(
    () => flightItems
      .filter(fi => (fi.item as App.Checkout.FlightItem).isClassicReward)
      .map(fi => {
        const flightCheckoutItem = fi.item as App.Checkout.FlightItem
        const associatedRoomCount = flightCheckoutItem.bundledItemIds?.length ?? 1

        if (associatedRoomCount <= 1 || !hotelItem?.occupancy) return flightCheckoutItem

        return {
          ...flightCheckoutItem,
          passengers: generatePassengersFromOccupants(hotelItem.occupancy),
        }
      }),
    [flightItems, hotelItem?.occupancy],
  )
  const rewardPricing = useClassicRewardCartPricing(rewardFlightCheckoutItems, { disabled: rewardFlightRefreshing })
  const rewardBundlePrice = useMemo(() => {
    if (!rewardPricing.available) return undefined
    return computeRewardBundlePrice({
      hotelPrice: hotelPublicTotal,
      hotelMemberPrice: hotelMemberTotal > 0 ? hotelMemberTotal : undefined,
      rewardPointsTotal: rewardPricing.totalPoints,
      rewardCashCoPayTotal: rewardPricing.totalCashCoPay,
    })
  }, [rewardPricing.available, rewardPricing.totalPoints, rewardPricing.totalCashCoPay, hotelPublicTotal, hotelMemberTotal])

  // Show loading when the bundle contains a reward flight but the final price isn't ready.
  const rewardPriceLoading = rewardPricing.isReward && !rewardBundlePrice

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, data.isUnavailable, !!data.error)
  const roomsLeft = data.isSoldOut ? 0 : (data.package?.availableRooms ?? 0)
  const image = data.package?.roomType.images[0] || data.offer?.image

  const isValueBeyondNonMemberPrice = (data.itemTotals?.value ?? 0) > hotelPublicTotal
  const shouldDisplayValueUpTo = isValueBeyondNonMemberPrice && (data.package?.shouldDisplayValue === true)

  // Check if all items are saved for later
  const areAllItemsSavedForLater = useMemo(
    () => multiCartItems.every(item => item.state === 'save_for_later'),
    [multiCartItems],
  )

  const isBookable = useMemo(
    () => !multiCartItems.some(item => item.state === 'deleted') && data.isBookable,
    [multiCartItems, data.isBookable],
  )

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => multiCartItems.map(item => item.id), [multiCartItems])

  // Get current states for all items
  const currentStates = useMemo(
    () => multiCartItems.reduce((acc, item) => {
      acc[item.id] = item.state
      return acc
    }, {} as Record<string, App.MultiCartItemState>),
    [multiCartItems],
  )

  useEffect(() => {
    // Only save if explicitly enabled AND item is not bookable AND not already saved/deleted
    if (
      !data.isBookable &&
      hotelItem?.state === 'active' && // Only process active items
      !data.loading &&
      activeCartId
    ) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId,
        itemIds: [hotelItem.id, ...flightItems.map(item => item.id)],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [data.isBookable, hotelItem?.state, data.loading, activeCartId, hotelItem?.id, flightItems, multiCartReviewActorRef])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: allItemIds,
      saveForLater: !areAllItemsSavedForLater,
    })
  }, [activeCartId, allItemIds, areAllItemsSavedForLater, multiCartReviewActorRef])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: allItemIds,
      currentStates,
    })
  }, [activeCartId, allItemIds, currentStates, multiCartActorRef])

  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.offer || !data.itemTotals) {
      return {
        calculationRequests: [],
        calculationType: 'exact',
      }
    }
    // Classic Rewards: the reward flight portion isn't earnable (points cover the
    // flight), so this hotel-tile earn is computed on the hotel portion alone to
    // match the cart-level breakdown. For cash bundles the bundled total is used.
    const earnPrice = rewardPricing.isReward ? hotelPublicTotal : bundledPublicTotal
    const earnMemberPrice = rewardPricing.isReward ? (hotelMemberTotal > 0 ? hotelMemberTotal : undefined) : bundledMemberTotal
    const calculationRequest = generateLoyaltyPointsCalculatorHotelOptions(data.offer, {
      pkg: data.package,
      price: earnPrice,
      memberPrice: earnMemberPrice,
      currentRegionCode,
      nights: data.package?.duration,
    })
    return {
      calculationRequests: [calculationRequest],
      calculationType: 'exact',
    }
  }, [data.offer, data.itemTotals, bundledPublicTotal, bundledMemberTotal, hotelPublicTotal, hotelMemberTotal, currentRegionCode, data.package, rewardPricing.isReward])

  if (data.loading || !hotelItem) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (tileVariant === 'condensed') {
    const condensedPriceElement = rewardPriceLoading ? (
      <TextLoadingBox width={120} />
    ) : rewardBundlePrice ? (
      <VerticalSpacer gap={4}>
        <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
          <Heading variant="heading6" colour={rewardBundlePrice.memberCash !== undefined ? 'lux-plus' : undefined}>
            <FormattedMessage
              id="V53dchYE9awxDiE72URx"
              defaultMessage="{points} + {cash}"
              description="Reward bundle price on the cart accommodation tile showing loyalty points plus cash co-pay"
              values={{
                points: <FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated />,
                cash: <FormatCurrency value={rewardBundlePrice.memberCash ?? rewardBundlePrice.cash} format="preciseDollar" />,
              }}
            />
          </Heading>
        </Group>
        {rewardBundlePrice.memberCash !== undefined && (
          <Caption variant="medium" colour="neutral-two">
            <FormattedMessage
              id="iipG8VyXTvvJj7Lza1MZ"
              defaultMessage="Non-member <b>{points} + {cash}</b>"
              description="Non-member reward price caption shown below the LuxPlus member price on the cart review tile"
              values={{
                points: <FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated />,
                cash: <FormatCurrency value={rewardBundlePrice.cash} format="preciseDollar" />,
                b: (chunks: React.ReactNode) => <b>{chunks}</b>,
              }}
            />
          </Caption>
        )}
      </VerticalSpacer>
    ) : (
      <CartReviewPriceStack
        price={bundledPublicTotal}
        memberPrice={bundledMemberTotal}
        value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />
    )

    return (
      <CondensedCartReviewTile
        label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
        content={<CondensedCartReviewTileContent
          title={data.offer?.property.name || data.offer?.name || ''}
          dates={formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
          roomName={data.package?.roomType.name}
        />}
        price={condensedPriceElement}
        banner={<CartReviewBanner variant={bannerTypeVariant} />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        onDelete={hideButtons ? undefined : handleDelete}
      />
    )
  }

  const actions = hideButtons ? undefined : (
    <CartReviewTileActions
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />
  )
  const fullPriceElement = rewardPriceLoading ? (
    <TextLoadingBox width={120} />
  ) : rewardBundlePrice ? (
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
        <Heading variant="heading6" colour={rewardBundlePrice.memberCash !== undefined ? 'lux-plus' : undefined}>
          <FormattedMessage
            id="V53dchYE9awxDiE72URx"
            defaultMessage="{points} + {cash}"
            description="Reward bundle price on the cart accommodation tile showing loyalty points plus cash co-pay"
            values={{
              points: <FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated />,
              cash: <FormatCurrency value={rewardBundlePrice.memberCash ?? rewardBundlePrice.cash} format="preciseDollar" />,
            }}
          />
        </Heading>
      </Group>
      {rewardBundlePrice.memberCash !== undefined && (
        <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="LhV2PlssNEZV4Vyv9DKf"
            defaultMessage="Non-member {points} + {cash}"
            description="Non-member reward price caption shown below the LuxPlus member price on the cart review tile"
            values={{
              points: <b><FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated /></b>,
              cash: <b><FormatCurrency value={rewardBundlePrice.cash} format="preciseDollar" /></b>,
            }}
          />
        </Caption>
      )}
    </VerticalSpacer>
  ) : (
    <CartReviewPriceStack
      price={bundledPublicTotal}
      memberPrice={bundledMemberTotal}
      value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
      luxPlusOfferConfig={data.offer?.luxPlus}
    />
  )

  return (
    <CartReviewTile
      label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        roomName={data.package?.roomType.name}
      />}
      price={fullPriceElement}
      banner={<CartReviewBanner variant={bannerTypeVariant} />}
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      actions={actions}
      hideAvailabilityDetails={false}
      disableOverlay={false}
    />
  )
}

export default LeHotelTile
